import React, { useState, useEffect, useRef } from 'react';
import { 
  Plus, Pill, Package, ScanBarcode, CheckCircle, XCircle, Search, Trash2, RotateCcw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Check, ChevronsUpDown } from 'lucide-react';
import api, { dispatchesAPI, projectsAPI, productsAPI, medicationsAPI, formulasAPI, settingsAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import { useSuperUser } from '../context/SuperUserContext';
import { useCanSeeAmounts } from '../context/PermissionsContext';
import { SuperUserDialog } from '../components/SuperUserDialog';
import {
  DispatchStats, DispatchFilters, DispatchesTable, DispatchViewDialog
} from '../components/dispatches';

// Unit conversion helpers
const UNIT_CONVERSIONS = {
  'kg': { 'kg': 1, 'lb': 2.20462, 'gramo': 1000, 'mg': 1000000 },
  'lb': { 'kg': 0.453592, 'lb': 1, 'gramo': 453.592, 'mg': 453592 },
  'gramo': { 'kg': 0.001, 'lb': 0.00220462, 'gramo': 1, 'mg': 1000 },
  'mg': { 'kg': 0.000001, 'lb': 0.00000220462, 'gramo': 0.001, 'mg': 1 },
  'litro': { 'litro': 1, 'ml': 1000 },
  'ml': { 'litro': 0.001, 'ml': 1 },
  'unidad': { 'unidad': 1 }
};

const convertQuantity = (quantity, fromUnit, toUnit) => {
  if (fromUnit === toUnit) return quantity;
  const conversions = UNIT_CONVERSIONS[fromUnit];
  return conversions && conversions[toUnit] ? quantity * conversions[toUnit] : quantity;
};

const getCompatibleUnits = (baseUnit) => {
  const conversions = UNIT_CONVERSIONS[baseUnit];
  return conversions ? Object.keys(conversions) : [baseUnit];
};

export default function DispatchesPage() {
  const [dispatches, setDispatches] = useState([]);
  const [projects, setProjects] = useState([]);
  const [products, setProducts] = useState([]);
  const [formulas, setFormulas] = useState([]);
  const [medications, setMedications] = useState([]);
  const [operationalCostPerQq, setOperationalCostPerQq] = useState(0);
  const [exchangeRate, setExchangeRate] = useState(62);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDispatch, setSelectedDispatch] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingDispatch, setEditingDispatch] = useState(null);
  
  const { isSuperUser } = useSuperUser();
  const canSeeDispatchAmounts = useCanSeeAmounts('dispatches');
  const [superUserDialogOpen, setSuperUserDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  
  // Filters
  const [filterProject, setFilterProject] = useState('');
  const [filterNave, setFilterNave] = useState('');
  const [filterLote, setFilterLote] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    project_id: '', project_name: '', animal_type: '', nave_number: 1, lote_number: '',
    dispatch_date: new Date().toISOString().split('T')[0], notes: '', items: []
  });
  
  const [isNewLote, setIsNewLote] = useState(false);
  const [newLoteValue, setNewLoteValue] = useState('');
  
  const [newItem, setNewItem] = useState({
    item_type: 'alimento', item_id: '', item_name: '', quantity: 0, unit: 'qq'
  });

  const [premixItem, setPremixItem] = useState({
    medication_id: '', quantity: '', output_unit: ''
  });

  // Tab superior de página: 'alimentos' (alimento + premix) o 'medicamentos' (solo no-premix)
  const [pageTab, setPageTab] = useState('alimentos');

  // Modo devolución: cuando true, el despacho SUMA al stock en vez de restar.
  const [isReturnMode, setIsReturnMode] = useState(false);

  // Clasificador de despacho: revisa items; si contiene alimento o medicamento premix => 'alimentos'; si es puramente medicamentos no-premix => 'medicamentos'.
  const getDispatchCategory = (dispatch) => {
    const items = dispatch.items || [];
    const hasAlimento = items.some(it => it.item_type === 'alimento');
    if (hasAlimento) return 'alimentos';
    const hasPremix = items.some(it => {
      if (it.is_premix) return true;
      const med = medications.find(m => m.id === it.item_id);
      return med?.administration_type === 'premix';
    });
    if (hasPremix) return 'alimentos';
    return 'medicamentos';
  };

  // Barcode scanner
  const [barcodeInput, setBarcodeInput] = useState('');
  const [barcodeSearchResult, setBarcodeSearchResult] = useState(null);
  const [barcodeSearching, setBarcodeSearching] = useState(false);
  const barcodeInputRef = useRef(null);

  const selectedProject = projects.find(p => p.id === formData.project_id);
  const availableNaves = selectedProject?.naves || [1];

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dispatchesRes, projectsRes, productsRes, medicationsRes, formulasRes] = await Promise.all([
        dispatchesAPI.getAll(),
        projectsAPI.getAll('activo'),
        productsAPI.getAll(),
        medicationsAPI.getAll(),
        formulasAPI.getAll(),
      ]);
      setDispatches(dispatchesRes.data);
      setProjects(projectsRes.data);
      setProducts(productsRes.data);
      setMedications(medicationsRes.data);
      setFormulas(formulasRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Cargar costo operativo y tasa de cambio (independiente y refrescable)
  const fetchSettings = async () => {
    try {
      const [opCostRes, rateRes] = await Promise.all([
        settingsAPI.getOperationalCost(),
        settingsAPI.getExchangeRate(),
      ]);
      setOperationalCostPerQq(opCostRes?.data?.cost_per_qq ?? 0);
      if (rateRes?.data?.rate) setExchangeRate(rateRes.data.rate);
    } catch (e) {
      console.warn('[DispatchesPage] No se pudo cargar settings:', e?.response?.status, e?.message);
    }
  };

  useEffect(() => { fetchData(); fetchSettings(); }, []);
  
  // Refrescar costo operativo y tasa cada vez que se abre el diálogo (para usar valores actualizados)
  useEffect(() => {
    if (dialogOpen) { fetchSettings(); }
  }, [dialogOpen]);

  const handleBarcodeSearch = async (barcode) => {
    if (!barcode || barcode.trim() === '') { setBarcodeSearchResult(null); return; }
    setBarcodeSearching(true);
    try {
      const response = await medicationsAPI.getByBarcode(barcode.trim());
      if (response.data) {
        setBarcodeSearchResult({ found: true, medication: response.data });
        setNewItem({ ...newItem, item_type: 'medicamento', item_id: response.data.id, item_name: response.data.name, unit: 'unidad' });
        toast.success(`Medicamento encontrado: ${response.data.name}`);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setBarcodeSearchResult({ found: false, message: 'No se encontró medicamento con este código' });
        toast.error('Medicamento no encontrado');
      } else { toast.error('Error al buscar medicamento'); }
    } finally { setBarcodeSearching(false); }
  };

  const handleBarcodeKeyPress = (e) => { if (e.key === 'Enter') { e.preventDefault(); handleBarcodeSearch(barcodeInput); } };
  const clearBarcodeResult = () => { setBarcodeInput(''); setBarcodeSearchResult(null); };

  const calculateDispatchTotal = (dispatch) => {
    if (!dispatch.items || dispatch.items.length === 0) return { dop: 0, usd: 0 };
    let totalDOP = 0, totalUSD = 0;
    dispatch.items.forEach(item => {
      if (item.item_type === 'alimento') {
        // Usar el precio guardado en el item (momento del despacho)
        if (item.unit_price_usd) {
          totalUSD += item.quantity * item.unit_price_usd;
          totalDOP += item.quantity * (item.unit_price_dop || item.unit_price_usd * 62);
        } else {
          // Fallback: usar costo de la fórmula (igual que tabla de productos)
          const product = products.find(p => p.id === item.item_id);
          if (product && product.formula_id) {
            const formula = formulas.find(f => f.id === product.formula_id);
            if (formula) {
              totalUSD += item.quantity * (formula.unit_cost_usd || 0);
              totalDOP += item.quantity * (formula.unit_cost_dop || (formula.unit_cost_usd || 0) * 62);
            } else if (product) {
              totalUSD += item.quantity * (product.price_usd || 0);
              totalDOP += item.quantity * (product.price_dop || (product.price_usd || 0) * 62);
            }
          } else if (product) {
            totalUSD += item.quantity * (product.price_usd || 0);
            totalDOP += item.quantity * (product.price_dop || (product.price_usd || 0) * 62);
          }
        }
      } else if (item.item_type === 'medicamento') {
        if (item.unit_price_usd) {
          totalUSD += item.quantity * item.unit_price_usd;
          totalDOP += item.quantity * (item.unit_price_dop || item.unit_price_usd * 62);
        } else {
          const medication = medications.find(m => m.id === item.item_id);
          if (medication) { 
            totalDOP += item.quantity * (medication.price_dop || 0); 
            totalUSD += item.quantity * (medication.price_usd || 0); 
          }
        }
      }
    });
    // Las devoluciones se restan del total facturado (valor negativo)
    if (dispatch.is_return) {
      return { dop: -totalDOP, usd: -totalUSD };
    }
    return { dop: totalDOP, usd: totalUSD };
  };

  const resetForm = () => {
    setFormData({ project_id: '', project_name: '', animal_type: '', nave_number: 1, lote_number: '', dispatch_date: new Date().toISOString().split('T')[0], notes: '', items: [] });
    setNewItem({ item_type: 'alimento', item_id: '', item_name: '', quantity: 0, unit: 'qq' });
    setBarcodeInput(''); setBarcodeSearchResult(null);
    setIsNewLote(false); setNewLoteValue('');
  };

  const handleAddItem = () => {
    if (!newItem.item_id || newItem.quantity <= 0) { toast.error('Selecciona un item y cantidad válida'); return; }
    let itemName = '';
    if (newItem.item_type === 'alimento') { itemName = products.find(p => p.id === newItem.item_id)?.name || ''; }
    else { itemName = medications.find(m => m.id === newItem.item_id)?.name || ''; }
    setFormData({ ...formData, items: [...formData.items, { ...newItem, item_name: itemName }] });
    setNewItem({ item_type: newItem.item_type, item_id: '', item_name: '', quantity: 0, unit: 'qq' });
  };

  const handleRemoveItem = (index) => { setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) }); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.project_id) { toast.error('Selecciona un proyecto y lote'); return; }
    
    // Determinar el número de lote final
    const finalLoteNumber = isNewLote ? newLoteValue : formData.lote_number;
    if (!finalLoteNumber || finalLoteNumber === '__nuevo__') { toast.error('Ingresa el número de lote'); return; }
    
    if (formData.items.length === 0) { toast.error('Agrega al menos un item al despacho'); return; }
    try {
      const dataToSend = { ...formData, lote_number: finalLoteNumber, is_return: isReturnMode };
      if (editingDispatch) { 
        await dispatchesAPI.update(editingDispatch.id, dataToSend); 
        toast.success('Despacho actualizado'); 
        setEditingDispatch(null); 
      } else { 
        const response = await dispatchesAPI.create(dataToSend); 
        const newDispatch = response.data;
        toast.success(isReturnMode ? 'Devolución registrada' : 'Despacho registrado'); 
        // Abrir ventana de impresión directamente
        if (newDispatch && newDispatch.id) {
          try {
            const pdfResponse = await api.get(`/dispatches/${newDispatch.id}/pdf`, { responseType: 'blob' });
            const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            
            // Abrir PDF en nueva ventana e imprimir
            const printWindow = window.open(url, '_blank');
            if (printWindow) {
              printWindow.onload = () => {
                setTimeout(() => {
                  printWindow.print();
                }, 500);
              };
            } else {
              // Si el popup está bloqueado, abrir en iframe oculto
              const iframe = document.createElement('iframe');
              iframe.style.display = 'none';
              iframe.src = url;
              document.body.appendChild(iframe);
              iframe.onload = () => {
                setTimeout(() => {
                  iframe.contentWindow.print();
                  setTimeout(() => {
                    document.body.removeChild(iframe);
                    window.URL.revokeObjectURL(url);
                  }, 1000);
                }, 500);
              };
            }
          } catch (pdfError) {
            console.error('Error al generar PDF:', pdfError);
            toast.error('Error al generar PDF');
          }
        }
      }
      resetForm(); setDialogOpen(false); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al guardar despacho'); }
  };

  const handleEdit = (dispatch) => {
    if (!isSuperUser) { setPendingAction({ type: 'edit', data: dispatch }); setSuperUserDialogOpen(true); return; }
    executeEdit(dispatch);
  };

  const executeEdit = (dispatch) => {
    // Ajustar pageTab según la categoría del despacho que se edita
    const cat = getDispatchCategory(dispatch);
    setPageTab(cat);
    setIsReturnMode(!!dispatch.is_return);
    setNewItem({
      item_type: cat === 'medicamentos' ? 'medicamento' : 'alimento',
      item_id: '', item_name: '', quantity: 0,
      unit: cat === 'medicamentos' ? 'unidad' : 'qq',
    });
    setEditingDispatch(dispatch);
    setFormData({ project_id: dispatch.project_id, nave_number: dispatch.nave_number, lote_number: dispatch.lote_number,
      dispatch_date: dispatch.dispatch_date?.split('T')[0] || new Date().toISOString().split('T')[0], notes: dispatch.notes || '', items: dispatch.items || [] });
    setDialogOpen(true);
  };

  const handleDelete = (id) => {
    if (!isSuperUser) { setPendingAction({ type: 'delete', data: id }); setSuperUserDialogOpen(true); return; }
    executeDelete(id);
  };

  const executeDelete = async (id) => {
    try { await dispatchesAPI.delete(id); toast.success('Despacho eliminado'); fetchData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Error al eliminar'); }
  };

  const handleSuperUserVerified = () => {
    if (!pendingAction) return;
    switch (pendingAction.type) {
      case 'edit': executeEdit(pendingAction.data); break;
      case 'delete': executeDelete(pendingAction.data); break;
      default: break;
    }
    setPendingAction(null);
  };

  // Filter options - filtrar por NOMBRE de proyecto para agrupar todos los ciclos del mismo proyecto
  const navesForFilter = filterProject ? [...new Set(dispatches.filter(d => d.project_name === filterProject).map(d => d.nave_number))].sort((a, b) => a - b) : [];
  const lotesForFilter = filterProject ? [...new Set(dispatches.filter(d => d.project_name === filterProject).filter(d => !filterNave || d.nave_number === parseInt(filterNave)).map(d => d.lote_number))].sort() : [];

  const filteredDispatches = dispatches.filter(dispatch => {
    const matchesSearch = dispatch.dispatch_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dispatch.project_name.toLowerCase().includes(searchTerm.toLowerCase()) || dispatch.lote_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProject = !filterProject || dispatch.project_name === filterProject;
    const matchesNave = !filterNave || dispatch.nave_number === parseInt(filterNave);
    const matchesLote = !filterLote || dispatch.lote_number === filterLote;
    const dispatchDate = dispatch.dispatch_date?.split('T')[0];
    const matchesDateFrom = !filterDateFrom || dispatchDate >= filterDateFrom;
    const matchesDateTo = !filterDateTo || dispatchDate <= filterDateTo;
    return matchesSearch && matchesProject && matchesNave && matchesLote && matchesDateFrom && matchesDateTo;
  });

  // Separar los despachos por categoría (Alimentos vs Medicamentos)
  const alimentosDispatches = filteredDispatches.filter(d => getDispatchCategory(d) === 'alimentos');
  const medicamentosDispatches = filteredDispatches.filter(d => getDispatchCategory(d) === 'medicamentos');
  const dispatchesForTab = pageTab === 'alimentos' ? alimentosDispatches : medicamentosDispatches;

  // Stats por tab activa
  const totalDispatches = dispatchesForTab.length;
  const totalQuantity = dispatchesForTab.reduce((sum, d) => sum + (d.items || []).filter(i => i.item_type === 'alimento').reduce((s, i) => s + (i.quantity || 0), 0), 0);
  const uniqueLotes = [...new Set(dispatchesForTab.map(d => d.lote_number))].length;
  const totalValueFiltered = dispatchesForTab.reduce((sum, dispatch) => { const totals = calculateDispatchTotal(dispatch); return { dop: sum.dop + totals.dop, usd: sum.usd + totals.usd }; }, { dop: 0, usd: 0 });

  // Total consolidado (todas las tabs)
  const consolidatedTotal = filteredDispatches.reduce((sum, dispatch) => { const totals = calculateDispatchTotal(dispatch); return { dop: sum.dop + totals.dop, usd: sum.usd + totals.usd }; }, { dop: 0, usd: 0 });
  const alimentosTotal = alimentosDispatches.reduce((sum, dispatch) => { const totals = calculateDispatchTotal(dispatch); return { dop: sum.dop + totals.dop, usd: sum.usd + totals.usd }; }, { dop: 0, usd: 0 });
  const medicamentosTotal = medicamentosDispatches.reduce((sum, dispatch) => { const totals = calculateDispatchTotal(dispatch); return { dop: sum.dop + totals.dop, usd: sum.usd + totals.usd }; }, { dop: 0, usd: 0 });

  if (loading) {
    return (<div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-4 border-[#931A25] border-t-transparent rounded-full"></div></div>);
  }

  return (
    <div className="space-y-6" data-testid="dispatches-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Despachos</h1>
          <p className="text-slate-500 mt-1">Registro de despachos internos separados por alimento y medicamentos</p>
        </div>
        <div className="flex gap-2">
          {pageTab === 'medicamentos' && (
            <Button
              variant="outline"
              className="border-amber-400 text-amber-700 hover:bg-amber-50"
              onClick={() => {
                setIsReturnMode(true);
                setNewItem({ item_type: 'medicamento', item_id: '', item_name: '', quantity: 0, unit: 'unidad' });
                setDialogOpen(true);
              }}
              data-testid="new-return-btn"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Devolución
            </Button>
          )}
          <Button
            className={pageTab === 'medicamentos'
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-green-600 hover:bg-green-700 text-white'}
            onClick={() => {
              setIsReturnMode(false);
              // Preseleccionar el tipo de item según la tab activa
              setNewItem({
                item_type: pageTab === 'medicamentos' ? 'medicamento' : 'alimento',
                item_id: '', item_name: '', quantity: 0,
                unit: pageTab === 'medicamentos' ? 'unidad' : 'qq',
              });
              setDialogOpen(true);
            }}
            data-testid="new-dispatch-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Despacho {pageTab === 'alimentos' ? 'de Alimento' : 'de Medicamento'}
          </Button>
        </div>
      </div>

      {/* Consolidado + Tabs de página (solo visible si el usuario tiene permiso) */}
      {canSeeDispatchAmounts && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-gradient-to-br from-green-50 to-emerald-100 border border-green-200 rounded-lg p-4">
          <p className="text-xs text-green-700 font-medium uppercase">Alimentos ({alimentosDispatches.length})</p>
          <p className="text-2xl font-bold text-green-800 mt-1">{formatCurrency(alimentosTotal.usd, 'USD')}</p>
          <p className="text-xs text-green-600">({formatCurrency(alimentosTotal.dop)})</p>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-sky-100 border border-blue-200 rounded-lg p-4">
          <p className="text-xs text-blue-700 font-medium uppercase">Medicamentos ({medicamentosDispatches.length})</p>
          <p className="text-2xl font-bold text-blue-800 mt-1">{formatCurrency(medicamentosTotal.usd, 'USD')}</p>
          <p className="text-xs text-blue-600">({formatCurrency(medicamentosTotal.dop)})</p>
        </div>
        <div className="bg-gradient-to-br from-[#931A25]/10 to-[#E97171]/20 border border-[#931A25]/30 rounded-lg p-4">
          <p className="text-xs text-[#931A25] font-medium uppercase">Total Consolidado ({filteredDispatches.length})</p>
          <p className="text-2xl font-bold text-[#931A25] mt-1">{formatCurrency(consolidatedTotal.usd, 'USD')}</p>
          <p className="text-xs text-[#931A25]/80">({formatCurrency(consolidatedTotal.dop)})</p>
        </div>
      </div>
      )}

      <Tabs value={pageTab} onValueChange={setPageTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 h-12" data-testid="dispatches-page-tabs">
          <TabsTrigger value="alimentos" className="flex items-center gap-2 data-[state=active]:bg-green-100 data-[state=active]:text-green-800" data-testid="tab-alimentos">
            <Package className="h-4 w-4" />
            Alimentos
            <span className="ml-1 text-xs bg-green-200/60 text-green-800 rounded-full px-2 py-0.5">{alimentosDispatches.length}</span>
          </TabsTrigger>
          <TabsTrigger value="medicamentos" className="flex items-center gap-2 data-[state=active]:bg-blue-100 data-[state=active]:text-blue-800" data-testid="tab-medicamentos">
            <Pill className="h-4 w-4" />
            Medicamentos
            <span className="ml-1 text-xs bg-blue-200/60 text-blue-800 rounded-full px-2 py-0.5">{medicamentosDispatches.length}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="alimentos" className="mt-4 space-y-4">
          <DispatchStats totalDispatches={totalDispatches} totalQuantity={totalQuantity} totalValueFiltered={totalValueFiltered} uniqueLotes={uniqueLotes} isFiltered={!!filterProject} />
          <DispatchFilters filterDateFrom={filterDateFrom} setFilterDateFrom={setFilterDateFrom} filterDateTo={filterDateTo} setFilterDateTo={setFilterDateTo}
            filterProject={filterProject} setFilterProject={setFilterProject} filterNave={filterNave} setFilterNave={setFilterNave}
            filterLote={filterLote} setFilterLote={setFilterLote} searchTerm={searchTerm} setSearchTerm={setSearchTerm}
            projects={projects} navesForFilter={navesForFilter} lotesForFilter={lotesForFilter}
            onClear={() => { setFilterProject(''); setFilterNave(''); setFilterLote(''); setSearchTerm(''); setFilterDateFrom(''); setFilterDateTo(''); }} />
          <DispatchesTable filteredDispatches={alimentosDispatches} medications={medications} products={products} formulas={formulas} projects={projects} isSuperUser={isSuperUser}
            onView={(d) => { setSelectedDispatch(d); setViewDialogOpen(true); }} onEdit={handleEdit} onDelete={handleDelete} calculateDispatchTotal={calculateDispatchTotal} />
        </TabsContent>

        <TabsContent value="medicamentos" className="mt-4 space-y-4">
          <DispatchStats totalDispatches={totalDispatches} totalQuantity={totalQuantity} totalValueFiltered={totalValueFiltered} uniqueLotes={uniqueLotes} isFiltered={!!filterProject} />
          <DispatchFilters filterDateFrom={filterDateFrom} setFilterDateFrom={setFilterDateFrom} filterDateTo={filterDateTo} setFilterDateTo={setFilterDateTo}
            filterProject={filterProject} setFilterProject={setFilterProject} filterNave={filterNave} setFilterNave={setFilterNave}
            filterLote={filterLote} setFilterLote={setFilterLote} searchTerm={searchTerm} setSearchTerm={setSearchTerm}
            projects={projects} navesForFilter={navesForFilter} lotesForFilter={lotesForFilter}
            onClear={() => { setFilterProject(''); setFilterNave(''); setFilterLote(''); setSearchTerm(''); setFilterDateFrom(''); setFilterDateTo(''); }} />
          <DispatchesTable filteredDispatches={medicamentosDispatches} medications={medications} products={products} formulas={formulas} projects={projects} isSuperUser={isSuperUser}
            onView={(d) => { setSelectedDispatch(d); setViewDialogOpen(true); }} onEdit={handleEdit} onDelete={handleDelete} calculateDispatchTotal={calculateDispatchTotal} />
        </TabsContent>
      </Tabs>

      {/* New/Edit Dispatch Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) { resetForm(); setEditingDispatch(null); setIsReturnMode(false); } setDialogOpen(open); }}>
        <DialogContent className={`max-w-5xl max-h-[90vh] overflow-y-auto ${isReturnMode ? 'border-t-4 border-t-amber-400' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isReturnMode && <RotateCcw className="h-5 w-5 text-amber-600" />}
              {editingDispatch
                ? (editingDispatch.is_return ? 'Editar Devolución' : 'Editar Despacho')
                : (isReturnMode ? 'Nueva Devolución de Medicamento' : 'Nuevo Despacho')}
            </DialogTitle>
            <DialogDescription>
              {editingDispatch
                ? `Editando ${editingDispatch.is_return ? 'devolución' : 'despacho'} ${editingDispatch.dispatch_number}`
                : (isReturnMode
                    ? 'Registra medicamentos que están regresando al inventario. Se sumará al stock y se restará de lo facturado al lote.'
                    : 'Registra un despacho interno de alimento o medicamentos.')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Proyecto *</Label>
                {(() => {
                  // Filtrar proyectos activos y obtener nombres únicos
                  const activeProjects = projects.filter(p => p.status === 'activo');
                  const uniqueProjectNames = [...new Set(activeProjects.map(p => p.name))];
                  
                  return (
                    <Select value={formData.project_name || ''} onValueChange={(v) => {
                      // Al seleccionar un nombre de proyecto, buscar el animal_type del primer proyecto con ese nombre
                      const projectsWithName = activeProjects.filter(p => p.name === v);
                      const animalType = projectsWithName[0]?.animal_type;
                      setFormData({...formData, project_name: v, project_id: '', lote_number: '', nave_number: 1, animal_type: animalType});
                      setIsNewLote(false);
                      setNewLoteValue('');
                    }}>
                      <SelectTrigger data-testid="dispatch-project-select"><SelectValue placeholder="Seleccionar proyecto" /></SelectTrigger>
                      <SelectContent>
                        {uniqueProjectNames.length === 0 ? (
                          <div className="px-2 py-1.5 text-xs text-slate-500">No hay proyectos activos</div>
                        ) : (
                          uniqueProjectNames.map(name => {
                            const proj = activeProjects.find(p => p.name === name);
                            return (
                              <SelectItem key={name} value={name}>
                                {name} {proj?.animal_type && `(${proj.animal_type === 'pollo' ? 'Pollo' : proj.animal_type === 'gallina' ? 'Gallina' : proj.animal_type === 'cerdo' ? 'Cerdo' : 'Becerro'})`}
                              </SelectItem>
                            );
                          })
                        )}
                      </SelectContent>
                    </Select>
                  );
                })()}
              </div>
              <div className="space-y-2">
                <Label>Lote # *</Label>
                {(() => {
                  // Obtener todos los lotes de proyectos activos con el nombre seleccionado
                  const activeProjects = projects.filter(p => p.status === 'activo' && p.name === formData.project_name);
                  const allLotes = [];
                  activeProjects.forEach(proj => {
                    (proj.lotes || []).forEach(lote => {
                      allLotes.push({
                        ...lote,
                        project_id: proj.id,
                        project_animal: proj.animal_type
                      });
                    });
                  });
                  
                  return (
                    <div className="space-y-1">
                      {!isNewLote ? (
                        <Select 
                          value={formData.lote_number} 
                          onValueChange={(v) => {
                            if (v === '__nuevo__') {
                              setIsNewLote(true);
                              setNewLoteValue('');
                            } else {
                              // Encontrar el lote y su proyecto
                              const selectedLote = allLotes.find(l => l.number === v);
                              setFormData({
                                ...formData, 
                                lote_number: v, 
                                project_id: selectedLote?.project_id || '',
                                nave_number: 1
                              });
                            }
                          }}
                          disabled={!formData.project_name}
                        >
                          <SelectTrigger data-testid="dispatch-lote-select">
                            <SelectValue placeholder="Seleccionar lote..." />
                          </SelectTrigger>
                          <SelectContent>
                            {allLotes.length > 0 ? (
                              <>
                                {allLotes.map(lote => (
                                  <SelectItem key={`${lote.project_id}-${lote.number}`} value={lote.number}>
                                    Lote {lote.number} (Naves: {lote.naves?.join(', ') || 'N/A'})
                                  </SelectItem>
                                ))}
                              </>
                            ) : (
                              <div className="px-2 py-1.5 text-xs text-amber-600">
                                No hay lotes definidos. Créalos en Proyectos.
                              </div>
                            )}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="flex gap-2">
                          <Input 
                            value={newLoteValue}
                            onChange={(e) => setNewLoteValue(e.target.value)}
                            placeholder="Escribir número de lote"
                            autoFocus
                            data-testid="dispatch-lote-input"
                          />
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setIsNewLote(false);
                              setNewLoteValue('');
                            }}
                          >
                            X
                          </Button>
                        </div>
                      )}
                      {allLotes.length === 0 && formData.project_name && !isNewLote && (
                        <p className="text-xs text-amber-600">Primero crea los lotes en la sección de Proyectos.</p>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="space-y-2">
                <Label>Nave *</Label>
                {(() => {
                  // Obtener naves del lote seleccionado
                  const activeProjects = projects.filter(p => p.status === 'activo' && p.name === formData.project_name);
                  let selectedLote = null;
                  activeProjects.forEach(proj => {
                    const lote = proj.lotes?.find(l => l.number === formData.lote_number);
                    if (lote) selectedLote = lote;
                  });
                  const loteNaves = selectedLote?.naves || [];
                  
                  return (
                    <Select 
                      value={formData.nave_number.toString()} 
                      onValueChange={(v) => setFormData({...formData, nave_number: parseInt(v)})} 
                      disabled={!formData.lote_number || loteNaves.length === 0}
                    >
                      <SelectTrigger data-testid="dispatch-nave-select">
                        <SelectValue placeholder="Seleccionar nave" />
                      </SelectTrigger>
                      <SelectContent>
                        {loteNaves.length > 0 ? (
                          loteNaves.map(nave => (
                            <SelectItem key={nave} value={nave.toString()}>Nave {nave}</SelectItem>
                          ))
                        ) : (
                          <div className="px-2 py-1.5 text-xs text-slate-500">
                            Selecciona un lote primero
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                  );
                })()}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Fecha de Despacho</Label>
              <Input type="date" value={formData.dispatch_date} onChange={(e) => setFormData({...formData, dispatch_date: e.target.value})} />
            </div>

            {/* Add Items */}
            <div className="border rounded-lg p-4 space-y-4 bg-slate-50">
              <h4 className="font-medium">Agregar Items al Despacho</h4>
              <Tabs value={newItem.item_type} onValueChange={(v) => setNewItem({...newItem, item_type: v, item_id: ''})}>
                {pageTab === 'alimentos' ? (
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="alimento" className="flex items-center gap-2"><Package className="h-4 w-4" />Alimento</TabsTrigger>
                    <TabsTrigger value="premix" className="flex items-center gap-2"><Pill className="h-4 w-4 text-purple-600" />Premix</TabsTrigger>
                  </TabsList>
                ) : (
                  <TabsList className="grid w-full grid-cols-1">
                    <TabsTrigger value="medicamento" className="flex items-center gap-2"><Pill className="h-4 w-4" />Medicamento (Oral / Inyectable / Otros)</TabsTrigger>
                  </TabsList>
                )}
                
                <TabsContent value="alimento" className="mt-4">
                  <div className="grid grid-cols-3 gap-3">
                    <Select value={newItem.item_id} onValueChange={(v) => setNewItem({...newItem, item_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar alimento" /></SelectTrigger>
                      <SelectContent>
                        {(() => {
                          const projectAnimal = formData.animal_type;
                          // Filtrar productos por animal del proyecto
                          const filteredProducts = projectAnimal 
                            ? products.filter(prod => {
                                const productAnimal = prod.animal_type || formulas.find(f => f.id === prod.formula_id)?.animal_type;
                                return productAnimal === projectAnimal;
                              })
                            : products;
                          
                          if (filteredProducts.length === 0 && projectAnimal) {
                            return <SelectItem value="no-products" disabled>No hay productos para {projectAnimal === 'pollo' ? 'Pollo' : projectAnimal === 'gallina' ? 'Gallina' : projectAnimal === 'cerdo' ? 'Cerdo' : 'Becerro'}</SelectItem>;
                          }
                          return filteredProducts.map(prod => <SelectItem key={prod.id} value={prod.id}>{prod.name}</SelectItem>);
                        })()}
                      </SelectContent>
                    </Select>
                    <Input type="number" step="0.01" min="0" value={newItem.quantity || ''} onChange={(e) => setNewItem({...newItem, quantity: parseFloat(e.target.value) || 0})} placeholder="Cantidad (qq)" data-testid="alimento-quantity-input" />
                    <Button type="button" onClick={handleAddItem} variant="outline" data-testid="add-alimento-btn"><Plus className="h-4 w-4 mr-1" /> Agregar</Button>
                  </div>

                  {/* Preview de precio unitario y subtotal en vivo */}
                  {(() => {
                    if (!newItem.item_id || newItem.item_type !== 'alimento') return null;
                    const product = products.find(p => p.id === newItem.item_id);
                    if (!product) return null;
                    const formula = product.formula_id ? formulas.find(f => f.id === product.formula_id) : null;
                    const formulaCostUsd = formula?.unit_cost_usd || 0;
                    const unitPriceUsd = formula ? (formulaCostUsd + operationalCostPerQq) : (product.price_usd || 0);
                    const unitPriceDop = unitPriceUsd * exchangeRate;
                    const qty = parseFloat(newItem.quantity) || 0;
                    const subtotalUsd = qty * unitPriceUsd;
                    const subtotalDop = qty * unitPriceDop;
                    return (
                      <div className="mt-3 bg-white border border-green-200 rounded-lg p-3">
                        <div className="grid grid-cols-4 gap-3 text-sm">
                          <div>
                            <p className="text-xs text-slate-500">Costo Fórmula/qq</p>
                            <p className="font-medium text-slate-800">{formatCurrency(formulaCostUsd, 'USD')}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">+ Costo Operativo</p>
                            <p className="font-medium text-slate-800">{formatCurrency(operationalCostPerQq, 'USD')}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Precio Unitario</p>
                            <p className="font-bold text-green-700">{formatCurrency(unitPriceUsd, 'USD')}</p>
                            <p className="text-xs text-slate-500">({formatCurrency(unitPriceDop)})</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Subtotal ({qty} qq)</p>
                            <p className="font-bold text-lg text-[#931A25]">{formatCurrency(subtotalUsd, 'USD')}</p>
                            <p className="text-xs text-blue-600">({formatCurrency(subtotalDop)})</p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  {(() => {
                    const projectAnimal = formData.animal_type;
                    if (projectAnimal) {
                      const animalLabel = projectAnimal === 'pollo' ? 'Pollo' : projectAnimal === 'gallina' ? 'Gallina' : projectAnimal === 'cerdo' ? 'Cerdo' : 'Becerro';
                      const animalColors = {
                        'pollo': 'bg-red-100 text-red-700 border-red-200',
                        'gallina': 'bg-green-100 text-green-700 border-green-200',
                        'cerdo': 'bg-pink-100 text-pink-700 border-pink-200',
                        'becerro': 'bg-amber-100 text-amber-700 border-amber-200'
                      };
                      return (
                        <p className={`mt-2 text-xs px-2 py-1 rounded border ${animalColors[projectAnimal] || 'bg-blue-100 text-blue-700'}`}>
                          Solo se muestran productos para: <strong>{animalLabel}</strong>
                        </p>
                      );
                    }
                    return null;
                  })()}
                </TabsContent>

                <TabsContent value="premix" className="mt-4">
                  <div className="space-y-4">
                    <Select value={premixItem.medication_id} onValueChange={(v) => { const med = medications.find(m => m.id === v); setPremixItem({...premixItem, medication_id: v, output_unit: med?.unit || ''}); }}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar medicamento premix" /></SelectTrigger>
                      <SelectContent>{medications.filter(m => m.administration_type === 'premix').map(med => (
                        <SelectItem key={med.id} value={med.id}><div className="flex items-center gap-2"><span className="inline-block w-2 h-2 rounded-full bg-purple-500"></span>{med.name} - Stock: {med.current_stock} {med.presentation} ({med.unit})</div></SelectItem>
                      ))}</SelectContent>
                    </Select>
                    {premixItem.medication_id && (() => {
                      const selectedMed = medications.find(m => m.id === premixItem.medication_id);
                      if (!selectedMed) return null;
                      const compatibleUnits = getCompatibleUnits(selectedMed.unit);
                      const outputUnit = premixItem.output_unit || selectedMed.unit;
                      const inputQty = parseFloat(premixItem.quantity) || 0;
                      const baseQty = convertQuantity(inputQty, outputUnit, selectedMed.unit);
                      const pricePerBaseUnit = selectedMed.price_usd / (selectedMed.presentation_quantity || 1);
                      const conversionFactor = UNIT_CONVERSIONS[selectedMed.unit]?.[outputUnit] || 1;
                      const pricePerOutputUnit = pricePerBaseUnit / conversionFactor;
                      // Calcular stock total en la unidad de salida
                      const stockInBaseUnit = selectedMed.current_stock * (selectedMed.presentation_quantity || 1);
                      const stockInOutputUnit = stockInBaseUnit * (UNIT_CONVERSIONS[selectedMed.unit]?.[outputUnit] || 1);
                      return (
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-3">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-purple-900">{selectedMed.name}</p>
                              <p className="text-sm text-purple-600">
                                Stock: {selectedMed.current_stock} {selectedMed.presentation} ({selectedMed.presentation_quantity} {selectedMed.unit} c/u)
                                <span className="font-semibold text-purple-800"> = {stockInOutputUnit.toFixed(2)} {outputUnit}</span>
                              </p>
                            </div>
                            <span className="bg-purple-200 text-purple-800 text-xs px-2 py-1 rounded">Premix</span>
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1"><Label className="text-xs text-purple-700">Cantidad a despachar</Label><Input type="number" step="0.01" min="0" value={premixItem.quantity} onChange={(e) => setPremixItem({...premixItem, quantity: e.target.value})} placeholder="Ej: 5" className="border-purple-300" /></div>
                            <div className="space-y-1"><Label className="text-xs text-purple-700">Unidad de salida</Label>
                              <Select value={outputUnit} onValueChange={(v) => setPremixItem({...premixItem, output_unit: v})}>
                                <SelectTrigger className="border-purple-300"><SelectValue /></SelectTrigger>
                                <SelectContent>{compatibleUnits.map(u => <SelectItem key={u} value={u}>{u === 'kg' ? 'Kilogramos (kg)' : u === 'lb' ? 'Libras (lb)' : u === 'gramo' ? 'Gramos (g)' : u === 'mg' ? 'Miligramos (mg)' : u === 'litro' ? 'Litros (L)' : u === 'ml' ? 'Mililitros (ml)' : u}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1"><Label className="text-xs text-purple-700">Precio/{outputUnit}</Label><div className="h-9 flex items-center px-3 bg-purple-100 rounded-md text-sm font-medium text-purple-800">{formatCurrency(pricePerOutputUnit, 'USD')}</div></div>
                          </div>
                          {inputQty > 0 && <div className="bg-white rounded p-2 border border-purple-200"><div className="flex justify-between text-sm"><span className="text-slate-600">{inputQty} {outputUnit} = {baseQty.toFixed(4)} {selectedMed.unit}</span><span className="font-semibold text-purple-700">Total: {formatCurrency(inputQty * pricePerOutputUnit, 'USD')}</span></div></div>}
                          <Button type="button" onClick={() => { if (premixItem.medication_id && inputQty > 0) { setFormData({...formData, items: [...formData.items, { item_type: 'medicamento', item_id: premixItem.medication_id, item_name: selectedMed.name, quantity: inputQty, unit: outputUnit, base_quantity: baseQty, base_unit: selectedMed.unit, unit_price: pricePerOutputUnit, is_premix: true }]}); setPremixItem({ medication_id: '', quantity: '', output_unit: '' }); toast.success('Premix agregado al despacho'); } else { toast.error('Ingresa la cantidad a despachar'); } }} className="w-full bg-purple-600 hover:bg-purple-700 text-white"><Plus className="h-4 w-4 mr-1" /> Agregar Premix al Despacho</Button>
                        </div>
                      );
                    })()}
                    {medications.filter(m => m.administration_type === 'premix').length === 0 && <div className="text-center py-4 text-slate-500"><Pill className="h-8 w-8 mx-auto mb-2 text-slate-300" /><p>No hay medicamentos premix registrados</p><p className="text-sm">Ve a Medicamentos y crea uno con tipo "Premix"</p></div>}
                  </div>
                </TabsContent>
                
                <TabsContent value="medicamento" className="mt-4 space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2"><ScanBarcode className="h-4 w-4 text-blue-600" /><span className="text-sm font-medium text-blue-800">Búsqueda por Código de Barras</span></div>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input ref={barcodeInputRef} value={barcodeInput} onChange={(e) => setBarcodeInput(e.target.value)} onKeyPress={handleBarcodeKeyPress} placeholder="Escanea o escribe el código de barras..." className="pr-10" data-testid="barcode-input" />
                        {barcodeInput && <button type="button" onClick={clearBarcodeResult} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><XCircle className="h-4 w-4" /></button>}
                      </div>
                      <Button type="button" variant="outline" onClick={() => handleBarcodeSearch(barcodeInput)} disabled={barcodeSearching || !barcodeInput} data-testid="barcode-search-btn">{barcodeSearching ? <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" /> : <Search className="h-4 w-4" />}</Button>
                    </div>
                    {barcodeSearchResult && <div className={`mt-2 text-sm flex items-center gap-2 ${barcodeSearchResult.found ? 'text-green-600' : 'text-red-600'}`}>{barcodeSearchResult.found ? <><CheckCircle className="h-4 w-4" /><span>Encontrado: <strong>{barcodeSearchResult.medication.name}</strong></span></> : <><XCircle className="h-4 w-4" /><span>{barcodeSearchResult.message}</span></>}</div>}
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {(() => {
                      const availableMeds = medications.filter(med => med.administration_type !== 'premix');
                      const selectedMed = availableMeds.find(m => m.id === newItem.item_id);
                      return (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              role="combobox"
                              className="justify-between font-normal"
                              data-testid="medication-select"
                            >
                              <span className="truncate">
                                {selectedMed ? selectedMed.name : 'Buscar medicamento...'}
                              </span>
                              <ChevronsUpDown className="h-4 w-4 opacity-50 ml-2 shrink-0" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="p-0 w-[380px]" align="start">
                            <Command
                              filter={(value, search) => {
                                // value es el id; hacemos match por el nombre del medicamento
                                const med = availableMeds.find(m => m.id === value);
                                if (!med) return 0;
                                const hay = `${med.name} ${med.barcode || ''} ${med.category || ''}`.toLowerCase();
                                return hay.includes(search.toLowerCase()) ? 1 : 0;
                              }}
                            >
                              <CommandInput placeholder="Escribe el nombre del medicamento..." data-testid="medication-search-input" />
                              <CommandList>
                                <CommandEmpty>No se encontró ningún medicamento.</CommandEmpty>
                                <CommandGroup>
                                  {availableMeds.map(med => (
                                    <CommandItem
                                      key={med.id}
                                      value={med.id}
                                      onSelect={(v) => {
                                        setNewItem({ ...newItem, item_id: v, item_name: med.name });
                                        clearBarcodeResult();
                                      }}
                                      data-testid={`medication-option-${med.id}`}
                                    >
                                      <Check className={`mr-2 h-4 w-4 ${newItem.item_id === med.id ? 'opacity-100' : 'opacity-0'}`} />
                                      <div className="flex-1 min-w-0">
                                        <div className="truncate font-medium">{med.name}</div>
                                        <div className="text-xs text-slate-500 flex gap-2">
                                          {med.category && <span>{med.category}</span>}
                                          {med.barcode && <span>· {med.barcode}</span>}
                                          <span className="ml-auto">Stock: {med.current_stock} {med.presentation}</span>
                                        </div>
                                      </div>
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      );
                    })()}
                    <Input type="number" step="0.01" min="0" value={newItem.quantity || ''} onChange={(e) => setNewItem({...newItem, quantity: parseFloat(e.target.value) || 0, unit: 'unidad'})} placeholder="Cantidad" data-testid="medication-quantity-input" />
                    <Button type="button" onClick={handleAddItem} variant="outline" data-testid="add-medication-btn"><Plus className="h-4 w-4 mr-1" /> Agregar</Button>
                  </div>

                  {/* Preview de precio unitario y subtotal para medicamentos */}
                  {(() => {
                    if (!newItem.item_id || newItem.item_type !== 'medicamento') return null;
                    const medication = medications.find(m => m.id === newItem.item_id);
                    if (!medication) return null;
                    const unitPriceUsd = medication.price_usd || 0;
                    const unitPriceDop = medication.price_dop || (unitPriceUsd * exchangeRate);
                    const qty = parseFloat(newItem.quantity) || 0;
                    const subtotalUsd = qty * unitPriceUsd;
                    const subtotalDop = qty * unitPriceDop;
                    return (
                      <div className="mt-3 bg-white border border-blue-200 rounded-lg p-3">
                        <div className="grid grid-cols-3 gap-3 text-sm">
                          <div>
                            <p className="text-xs text-slate-500">Stock disponible</p>
                            <p className="font-medium text-slate-800">{medication.current_stock} {medication.presentation}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Precio Unitario</p>
                            <p className="font-bold text-blue-700">{formatCurrency(unitPriceUsd, 'USD')}</p>
                            <p className="text-xs text-slate-500">({formatCurrency(unitPriceDop)})</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Subtotal ({qty} {newItem.unit || 'unidad'})</p>
                            <p className="font-bold text-lg text-[#931A25]">{formatCurrency(subtotalUsd, 'USD')}</p>
                            <p className="text-xs text-blue-600">({formatCurrency(subtotalDop)})</p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </TabsContent>
              </Tabs>

              {/* Items list */}
              {formData.items.length > 0 && (
                <div className="mt-4">
                  <h5 className="text-sm font-medium mb-2">Items a despachar:</h5>
                  <div className="space-y-2">
                    {formData.items.map((item, index) => {
                      let itemPrice = { dop: 0, usd: 0 };
                      if (item.is_premix && item.unit_price) { 
                        itemPrice.usd = item.quantity * item.unit_price; 
                        itemPrice.dop = itemPrice.usd * 62; 
                      }
                      else if (item.item_type === 'alimento') { 
                        const product = products.find(p => p.id === item.item_id); 
                        if (product?.formula_id) {
                          const formula = formulas.find(f => f.id === product.formula_id);
                          if (formula) {
                            const unitUsd = (formula.unit_cost_usd || 0) + operationalCostPerQq;
                            const unitDop = (formula.unit_cost_dop || (formula.unit_cost_usd || 0) * exchangeRate) + (operationalCostPerQq * exchangeRate);
                            itemPrice.usd = item.quantity * unitUsd;
                            itemPrice.dop = item.quantity * unitDop;
                          } else if (product) {
                            itemPrice.dop = item.quantity * (product.price_dop || 0); 
                            itemPrice.usd = item.quantity * (product.price_usd || 0);
                          }
                        } else if (product) { 
                          itemPrice.dop = item.quantity * (product.price_dop || 0); 
                          itemPrice.usd = item.quantity * (product.price_usd || 0); 
                        } 
                      }
                      else { 
                        const medication = medications.find(m => m.id === item.item_id); 
                        if (medication) { 
                          itemPrice.dop = item.quantity * (medication.price_dop || 0); 
                          itemPrice.usd = item.quantity * (medication.price_usd || 0); 
                        } 
                      }
                      return (
                        <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                          <div className="flex items-center gap-2">
                            {item.item_type === 'alimento' ? <Package className="h-4 w-4 text-green-600" /> : item.is_premix ? <Pill className="h-4 w-4 text-purple-600" /> : <Pill className="h-4 w-4 text-blue-600" />}
                            <span className="font-medium">{item.item_name}</span>
                            {item.is_premix && <span className="text-xs bg-purple-100 text-purple-700 px-1 rounded">Premix</span>}
                            <span className="text-slate-500">x {item.quantity} {item.unit}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm text-slate-600">{formatCurrency(itemPrice.usd, 'USD')}</span>
                            <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveItem(index)} className="text-red-600 hover:text-red-700"><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {(() => {
                    // Calcular desglose de totales una sola vez
                    let alimentoQqTotal = 0;      // qq totales de alimentos con fórmula (para costo operativo)
                    let costoAlimentoUsd = 0;      // Costo fórmula (sin operativo)
                    let costoMedicamentosUsd = 0;  // Medicamentos + premix
                    
                    formData.items.forEach(item => {
                      if (item.is_premix && item.unit_price) {
                        costoMedicamentosUsd += item.quantity * item.unit_price;
                      } else if (item.item_type === 'alimento') {
                        const product = products.find(p => p.id === item.item_id);
                        if (product?.formula_id) {
                          const formula = formulas.find(f => f.id === product.formula_id);
                          if (formula) {
                            costoAlimentoUsd += item.quantity * (formula.unit_cost_usd || 0);
                            alimentoQqTotal += item.quantity;
                          } else if (product) {
                            costoAlimentoUsd += item.quantity * (product.price_usd || 0);
                          }
                        } else if (product) {
                          costoAlimentoUsd += item.quantity * (product.price_usd || 0);
                        }
                      } else {
                        const medication = medications.find(m => m.id === item.item_id);
                        if (medication) costoMedicamentosUsd += item.quantity * (medication.price_usd || 0);
                      }
                    });
                    
                    const costoOperativoUsd = alimentoQqTotal * operationalCostPerQq;
                    const totalUsd = costoAlimentoUsd + costoOperativoUsd + costoMedicamentosUsd;
                    const totalDop = totalUsd * exchangeRate;
                    
                    return (
                      <div className="mt-4 p-4 bg-gradient-to-r from-[#931A25]/10 to-[#E97171]/10 rounded-lg border border-[#931A25]/20 space-y-2">
                        {/* Desglose */}
                        <div className="space-y-1 pb-2 border-b border-[#931A25]/20">
                          {costoAlimentoUsd > 0 && (
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-slate-600">Costo Alimento (fórmula):</span>
                              <span className="font-medium text-slate-800">{formatCurrency(costoAlimentoUsd, 'USD')}</span>
                            </div>
                          )}
                          {alimentoQqTotal > 0 && (
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-slate-600">
                                Costo Operativo:
                                <span className="text-xs text-slate-400 ml-1">({alimentoQqTotal.toFixed(2)} qq × {formatCurrency(operationalCostPerQq, 'USD')}/qq)</span>
                              </span>
                              <span className="font-medium text-slate-800">{formatCurrency(costoOperativoUsd, 'USD')}</span>
                            </div>
                          )}
                          {costoMedicamentosUsd > 0 && (
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-slate-600">Medicamentos / Premix:</span>
                              <span className="font-medium text-slate-800">{formatCurrency(costoMedicamentosUsd, 'USD')}</span>
                            </div>
                          )}
                        </div>
                        {/* Total */}
                        <div className="flex justify-between items-center pt-1">
                          <span className="font-semibold text-slate-700">Total del Despacho:</span>
                          <div className="text-right">
                            <p className="font-bold text-lg text-[#931A25]">{formatCurrency(totalUsd, 'USD')}</p>
                            <p className="text-sm text-blue-600">({formatCurrency(totalDop)})</p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} placeholder="Observaciones del despacho..." rows={2} />
            </div>

            <DialogFooter>
              <DialogClose asChild><Button type="button" variant="outline" onClick={resetForm}>Cancelar</Button></DialogClose>
              <Button type="submit" className="bg-[#931A25] hover:bg-[#7A151F]" disabled={formData.items.length === 0} data-testid="save-dispatch-btn">Registrar Despacho</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DispatchViewDialog open={viewDialogOpen} onOpenChange={setViewDialogOpen} dispatch={selectedDispatch} medications={medications} />

      <SuperUserDialog open={superUserDialogOpen} onOpenChange={setSuperUserDialogOpen} onSuccess={handleSuperUserVerified} title="Acción Restringida" description="Esta acción requiere verificación de super usuario." />
    </div>
  );
}
